import React, { useEffect, useState } from 'react';
import { api } from './auth';

const STATUS_COLOURS = {
  received:   { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A' },
  verifying:  { bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE' },
  in_progress:{ bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE' },
  forwarded:  { bg: '#F3E8FF', color: '#6B21A8', border: '#E9D5FF' },
  completed:  { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  rejected:   { bg: '#FEF2F2', color: '#991B1B', border: '#FECACA' },
};

const TYPE_LABELS = {
  access: 'Access', correction: 'Correction', deletion: 'Deletion', objection: 'Objection',
};

export default function AdminDataSubjectRequests({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // request being viewed
  const [searchResult, setSearchResult] = useState(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dsr/admin' + (filter !== 'all' ? `?status_filter=${filter}` : ''));
      setItems(r.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const view = async (id) => {
    setSearchResult(null);
    try {
      const r = await api.get(`/dsr/admin/${id}`);
      setSelected(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const search = async () => {
    if (!selected) return;
    setError(null);
    try {
      const r = await api.post(`/dsr/admin/${selected.id}/search`);
      setSearchResult(r.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const updateStatus = async (newStatus, summary, forwarded) => {
    if (!selected) return;
    try {
      const r = await api.patch(`/dsr/admin/${selected.id}/status`, {
        status: newStatus, response_summary: summary,
        forwarded_to_responsible_party: !!forwarded,
      });
      setSelected(r.data);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  if (user?.role !== 'super_admin') {
    return <div style={card}>
      <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>Only the Information Officer (super admin) can manage Data Subject Requests.</p>
    </div>;
  }

  // Detail view
  if (selected) {
    const sc = STATUS_COLOURS[selected.status] || STATUS_COLOURS.received;
    return (
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button onClick={() => { setSelected(null); setSearchResult(null); }} style={btnGhost}>← Back to list</button>
          <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 6, padding: '3px 10px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>
            {selected.status}
          </span>
        </div>

        <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0, marginBottom: 4 }}>{TYPE_LABELS[selected.request_type]} request</h2>
        <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 16px 0', fontFamily: 'monospace' }}>Token: {selected.request_token}</p>

        <Section title="Requester">
          <KV label="Email" value={selected.requester_email} />
          {selected.requester_name && <KV label="Name" value={selected.requester_name} />}
          {selected.requester_id_last4 && <KV label="ID (last 4)" value={`••••••••• ${selected.requester_id_last4}`} />}
          {selected.company_name && <KV label="Company they referenced" value={selected.company_name} />}
          <KV label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
        </Section>

        <Section title="Description">
          <pre style={{ background: '#F8F9FB', borderRadius: 8, padding: '10px 14px', fontSize: 13, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{selected.description}</pre>
        </Section>

        <Section title="Investigation">
          <p style={pHint}>Search what Eyez holds about this requester. Note that for Service 1 (Capture), Eyez operates zero-retention, only audit hashes are stored, not personal data.</p>
          <button onClick={search} style={btnSecondary}>Search Eyez systems</button>

          {searchResult && (
            <div style={{ marginTop: 14 }}>
              <div style={{ background: '#F8F9FB', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                <strong style={{ fontSize: 12 }}>Platform user account: </strong>
                {searchResult.found_user_account
                  ? <span style={{ color: '#16A34A', fontSize: 12 }}>FOUND: {searchResult.found_user_account.email} ({searchResult.found_user_account.role})</span>
                  : <span style={{ color: '#6B7280', fontSize: 12 }}>None, not a platform user</span>}
              </div>
              <div style={{ background: '#F8F9FB', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                <strong style={{ fontSize: 12 }}>Verifications they ran: </strong>
                <span style={{ fontSize: 12 }}>{searchResult.found_verifications.length}</span>
                {searchResult.found_verifications.length > 0 && (
                  <ul style={{ marginTop: 8, marginBottom: 0, fontSize: 11, color: '#6B7280' }}>
                    {searchResult.found_verifications.slice(0, 5).map((v, i) => (
                      <li key={i}>{v.filename} · {v.verdict} · {new Date(v.created_at).toLocaleDateString()}</li>
                    ))}
                    {searchResult.found_verifications.length > 5 && <li>… and {searchResult.found_verifications.length - 5} more</li>}
                  </ul>
                )}
              </div>
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '12px 14px' }}>
                <strong style={{ fontSize: 12, color: '#92400E' }}>Service 1 (Capture): </strong>
                <span style={{ fontSize: 12, color: '#92400E' }}>{searchResult.found_capture_events[0]?.info}</span>
              </div>
            </div>
          )}
        </Section>

        <Section title="Action">
          <p style={pHint}>
            For Operator-role data, forward the request to the customer (Responsible Party). For data where Eyez is the Responsible Party, action it directly.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button onClick={() => updateStatus('verifying', 'Verifying requester identity via email reply.')} style={btnSecondary}>Mark: Verifying identity</button>
            <button onClick={() => updateStatus('in_progress', 'Working on this request.')} style={btnSecondary}>Mark: In progress</button>
            <button onClick={() => {
              const reason = window.prompt('Which Responsible Party are you forwarding to? (this is logged in the response summary)');
              if (reason) updateStatus('forwarded', `Forwarded to: ${reason}`, true);
            }} style={btnSecondary}>Forward to Responsible Party</button>
            <button onClick={() => {
              const reason = window.prompt('Reason for rejection?');
              if (reason) updateStatus('rejected', `Rejected: ${reason}`);
            }} style={{ ...btnSecondary, color: '#DC2626' }}>Reject</button>
            <button onClick={() => {
              const summary = window.prompt('Final response summary?');
              if (summary) updateStatus('completed', summary);
            }} style={btnPrimary}>Mark as completed</button>
          </div>
          {selected.response_summary && (
            <div style={{ marginTop: 14 }}>
              <strong style={{ fontSize: 12, color: '#6B7280' }}>Response summary log:</strong>
              <pre style={{ background: '#F8F9FB', borderRadius: 8, padding: '10px 14px', fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: '6px 0 0 0', maxHeight: 200, overflowY: 'auto' }}>
                {selected.response_summary}
              </pre>
            </div>
          )}
        </Section>

        {selected.request_type === 'deletion' && selected.status !== 'completed' && (
          <Section title="Execute deletion (where Eyez is Responsible Party)">
            <p style={pHint}>This permanently anonymises the requester's data in Eyez's own systems. Requires email confirmation.</p>
            <DeleteActions request={selected} onDone={() => { setSelected(null); load(); }} />
          </Section>
        )}
      </div>
    );
  }

  // List view
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Data subject requests</h2>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0 0' }}>POPIA Chapter 3 Part B: access, correction, deletion, objection</p>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={inp}>
          <option value="all">All statuses</option>
          <option value="received">Received</option>
          <option value="verifying">Verifying</option>
          <option value="in_progress">In progress</option>
          <option value="forwarded">Forwarded</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? <p style={{ color: '#9CA3AF', fontSize: 13 }}>Loading…</p>
       : items.length === 0 ? (
         <div style={{ textAlign: 'center', padding: 30, color: '#9CA3AF' }}>
           <p style={{ fontSize: 13, margin: 0 }}>No requests yet.</p>
           <p style={{ fontSize: 12, margin: '4px 0 0 0' }}>Public submission endpoint: <code>/dsr/submit</code></p>
         </div>
       )
       : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#F8F9FB', textAlign: 'left' }}>
              <th style={th}>Received</th><th style={th}>Type</th><th style={th}>Requester</th>
              <th style={th}>Status</th><th style={th}>Forwarded</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {items.map(r => {
                const sc = STATUS_COLOURS[r.status] || STATUS_COLOURS.received;
                return <tr key={r.id} style={{ borderBottom: '0.5px solid #F3F4F6' }}>
                  <td style={td}>{new Date(r.created_at).toLocaleString()}</td>
                  <td style={td}>{TYPE_LABELS[r.request_type]}</td>
                  <td style={td}>{r.requester_email}<br/>
                    <span style={{ fontSize: 11, color: '#6B7280' }}>{r.requester_name || '-'}</span>
                  </td>
                  <td style={td}><span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 6, padding: '2px 8px', fontWeight: 600, fontSize: 11 }}>{r.status}</span></td>
                  <td style={td}>{r.forwarded_to_responsible_party ? <span style={{ color: '#16A34A' }}>✓</span> : '-'}</td>
                  <td style={td}><button onClick={() => view(r.id)} style={btnTinyLink}>Open</button></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      )}

      {error && <div style={errBox}>{error}</div>}
    </div>
  );
}

function DeleteActions({ request, onDone }) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteAccount, setDeleteAccount] = useState(true);
  const [anonymise, setAnonymise] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm('This is irreversible. Proceed with deletion?')) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/dsr/admin/${request.id}/execute-deletion`, {
        confirm_email: confirmEmail,
        delete_user_account: deleteAccount,
        anonymise_verifications: anonymise,
        notes,
      });
      alert('Deletion executed: ' + r.data.actions_taken.join(', '));
      onDone();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
      <div>
        <label style={{ fontSize: 11, color: '#6B7280' }}>Type the requester's email exactly to confirm:</label>
        <input value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} placeholder={request.requester_email} required style={inp} />
      </div>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={deleteAccount} onChange={e => setDeleteAccount(e.target.checked)} />
        Anonymise platform user account (if any)
      </label>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={anonymise} onChange={e => setAnonymise(e.target.checked)} />
        Anonymise verification records (filename, IPs, summaries)
      </label>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notes for the audit trail (optional)" style={inp} />
      {error && <div style={errBox}>{error}</div>}
      <button type="submit" disabled={busy || !confirmEmail} style={{ ...btnPrimary, background: '#DC2626' }}>
        {busy ? 'Executing…' : 'Execute deletion'}
      </button>
    </form>
  );
}

function Section({ title, children }) {
  return <div style={{ marginBottom: 18, paddingBottom: 14, borderBottom: '0.5px solid #F3F4F6' }}>
    <h3 style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</h3>
    {children}
  </div>;
}
function KV({ label, value }) {
  return <div style={{ display: 'flex', gap: 10, fontSize: 13, marginBottom: 4 }}>
    <span style={{ minWidth: 130, color: '#6B7280' }}>{label}</span>
    <span>{value}</span>
  </div>;
}

const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' };
const inp = { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const btnPrimary = { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 500, fontSize: 13, cursor: 'pointer' };
const btnSecondary = { background: '#fff', color: '#111827', border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 14px', fontWeight: 500, fontSize: 12, cursor: 'pointer' };
const btnGhost = { background: 'transparent', color: '#6B7280', border: 'none', padding: '4px 8px', cursor: 'pointer', fontSize: 13 };
const btnTinyLink = { background: 'transparent', color: '#2563EB', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500 };
const th = { padding: '10px 12px', fontWeight: 500, fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid #E5E7EB' };
const td = { padding: '10px 12px', color: '#111827', verticalAlign: 'top' };
const pHint = { fontSize: 12, color: '#6B7280', margin: 0, marginBottom: 10, lineHeight: 1.5 };
const errBox = { background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', color: '#DC2626', fontSize: 13, marginTop: 10 };
